// src/components/LoginModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import Logo from './Logo';
import { useAuth } from '../hooks/useAuth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '519915945574-okha8hcf2n7dfsn9mm688nuupul8iums.apps.googleusercontent.com';

export default function LoginModal({ onClose, onSuccess, defaultTab = 'login' }) {
  const { login, register, googleLogin } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const googleBtnRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
  });

  useEffect(() => {
    setError('');
    setSuccess('');
    // Keep email (user has already typed it) but clear everything else.
    // Without this reset, fields from the other tab (password, firstName, etc.)
    // hang around invisibly and cause confusing validation errors.
    setForm(f => ({
      firstName: '',
      lastName:  '',
      email:     f.email,
      phone:     '',
      password:  '',
      confirmPassword: '',
    }));
  }, [tab]);

  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  // Load Google Identity Services
  useEffect(() => {
    const initGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: false,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black',
        size: 'large',
        width: googleBtnRef.current.offsetWidth || 356,
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
      });
    };

    if (window.google) {
      initGoogle();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  }, []);

  // Re-render Google button when tab changes
  useEffect(() => {
    if (window.google && googleBtnRef.current) {
      setTimeout(() => {
        try {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: 'filled_black',
            size: 'large',
            width: googleBtnRef.current.offsetWidth || 356,
            text: 'continue_with',
            shape: 'rectangular',
          });
        } catch {}
      }, 50);
    }
  }, [tab]);

  const handleGoogleCallback = async (response) => {
    if (!response.credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setGoogleLoading(true);
    setError('');
    try {
      const r = await googleLogin(response.credential);
      if (r.success) {
        setSuccess('Welcome!');
        setTimeout(() => { onSuccess?.(); onClose(); }, 600);
      } else {
        setError(r.message || 'Google sign-in failed');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!form.email || !form.password) { setError('Email and password are required'); return; }
    if (tab === 'register') {
      if (!form.firstName) { setError('First name is required'); return; }
      if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
      if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    }
    setLoading(true);
    try {
      const r = tab === 'login'
        ? await login(form.email.trim(), form.password)
        : await register({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            password: form.password,
            marketingOptIn: true,
          });
      if (r.success) {
        setSuccess(tab === 'login' ? 'Welcome back!' : 'Account created!');
        setTimeout(() => { onSuccess?.(); onClose(); }, 800);
      } else {
        setError(r.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(3,10,20,0.85)',
        backdropFilter: 'blur(6px)', zIndex: 999,
        animation: 'fadeIn 0.2s ease',
      }} />

      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420,
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2140 100%)',
        border: '1px solid rgba(0,229,204,0.15)',
        borderRadius: 20, padding: 32,
        zIndex: 1000,
        boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
        animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.05)', border: 'none',
          color: 'rgba(224,240,240,0.5)', width: 32, height: 32,
          borderRadius: 8, cursor: 'pointer', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <Logo iconSize={40} badge="SHOP" noLink />
          </div>
          <div style={{ color: 'rgba(224,240,240,0.4)', fontSize: 13 }}>
            {tab === 'login' ? 'Sign in to your account' : 'Create your account'}
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', background: 'rgba(255,255,255,0.04)',
          borderRadius: 10, padding: 3, marginBottom: 24,
        }}>
          {['login', 'register'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
              background: tab === t ? 'rgba(0,229,204,0.12)' : 'transparent',
              color: tab === t ? '#00E5CC' : 'rgba(224,240,240,0.4)',
              boxShadow: tab === t ? 'inset 0 0 0 1px rgba(0,229,204,0.2)' : 'none',
            }}>
              {t === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        {/* Google button */}
        <div style={{ marginBottom: 16 }}>
          {googleLoading ? (
            <div style={{
              width: '100%', padding: '11px 0', borderRadius: 10,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              textAlign: 'center', color: 'rgba(224,240,240,0.5)', fontSize: 13,
            }}>
              Signing in with Google...
            </div>
          ) : (
            <div ref={googleBtnRef} style={{ width: '100%', minHeight: 44, borderRadius: 10, overflow: 'hidden' }} />
          )}
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(224,240,240,0.25)', letterSpacing: '1px' }}>OR</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {/* Error / Success */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            color: '#f87171', fontSize: 13,
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            background: 'rgba(34,217,127,0.1)', border: '1px solid rgba(34,217,127,0.2)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            color: '#22D97F', fontSize: 13, textAlign: 'center', fontWeight: 600,
          }}>✓ {success}</div>
        )}

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tab === 'register' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input placeholder="First Name *" value={form.firstName} onChange={v => set('firstName', v)} />
              <Input placeholder="Last Name" value={form.lastName} onChange={v => set('lastName', v)} />
            </div>
          )}
          <Input placeholder="Email *" type="email" value={form.email} onChange={v => set('email', v)} />
          {tab === 'register' && (
            <Input placeholder="Phone" type="tel" value={form.phone} onChange={v => set('phone', v)} />
          )}
          <Input placeholder="Password *" type="password" value={form.password} onChange={v => set('password', v)} onEnter={tab === 'login' ? handleSubmit : undefined} />
          {tab === 'register' && (
            <Input placeholder="Confirm Password *" type="password" value={form.confirmPassword} onChange={v => set('confirmPassword', v)} onEnter={handleSubmit} />
          )}
        </div>

        <button onClick={handleSubmit} disabled={loading} style={{
          width: '100%', marginTop: 20, padding: '14px 0',
          background: loading ? 'rgba(0,229,204,0.3)' : 'linear-gradient(135deg, #00E5CC, #22D97F)',
          border: 'none', borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer',
          color: '#030A14', fontSize: 15, fontWeight: 700,
          transition: 'all 0.2s',
        }}>
          {loading ? '...' : tab === 'login' ? 'Sign In' : 'Create Account'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'rgba(224,240,240,0.4)' }}>
          {tab === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <span onClick={() => setTab(tab === 'login' ? 'register' : 'login')}
            style={{ color: '#00E5CC', cursor: 'pointer', fontWeight: 600 }}>
            {tab === 'login' ? 'Register' : 'Sign In'}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translate(-50%, -48%) scale(0.96) } to { opacity: 1; transform: translate(-50%, -50%) scale(1) } }
      `}</style>
    </>
  );
}

function Input({ placeholder, type = 'text', value, onChange, onEnter }) {
  return (
    <input
      type={type} placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter' && onEnter) onEnter(); }}
      style={{
        width: '100%', padding: '11px 14px', boxSizing: 'border-box',
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10, color: '#E0F0F0', fontSize: 14, outline: 'none',
        transition: 'border-color 0.2s',
      }}
      onFocus={e => e.target.style.borderColor = 'rgba(0,229,204,0.4)'}
      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
    />
  );
}